import * as React from 'react'
import axios from 'axios'
import TextField from '@mui/material/TextField'
import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import {
    Box,
    Divider,
    Grid,
    useMediaQuery,
} from '@mui/material'
import { ClientsContext } from '../context/clientsContext'
import { DialogsContext } from '../context/dialogsContext'
import { LoadingButton } from '@mui/lab'
import ConfirmDialog from './ConfirmDialog'
import { theme } from './SessionsTab/AreaStateSetterStyle'
import AlertBanner, { AlertBannerType } from './AlertBanner'
import { UserContext } from '../context/userContext'

export default function ClientMenuDialog() {
    const { clientMenuDialog, setClientMenuDialog } = React.useContext(DialogsContext);
    const {
        getClient,
        getClientRemovalPreview,
        removeClient,
        restoreClient,
        cohortId,
        scopeKey,
    } = React.useContext(ClientsContext);
    const { user } = React.useContext(UserContext);
    const cohortClientsEnabled = user.features?.heal_cohort_clients ?? false;
    const [loading, setLoading] = React.useState(false);
    const client = clientMenuDialog.clientId ? getClient(clientMenuDialog.clientId) : undefined;
    const sessionKey = `${scopeKey}:${clientMenuDialog.clientId}:${clientMenuDialog.show}`;
    const sessionRef = React.useRef({ key: sessionKey });
    if (sessionRef.current.key !== sessionKey) sessionRef.current = { key: sessionKey };
    const mounted = React.useRef(true);
    const previousScope = React.useRef(scopeKey);
    type Target = { clientId: number; cohortId: number; session: { key: string } };
    const [confirmation, setConfirmation] = React.useState<{ target: Target; message: string } | null>(null);
    const [notice, setNotice] = React.useState<string | null>(null);
    const [noticeType, setNoticeType] = React.useState(AlertBannerType.success);
    const isSmDown = useMediaQuery(theme.breakpoints.down('sm'));

    React.useEffect(() => {
        mounted.current = true;
        return () => { mounted.current = false; };
    }, []);

    React.useEffect(() => {
        setConfirmation(null);
        setLoading(false);
        if (clientMenuDialog.show) setNotice(null);
        if (previousScope.current !== scopeKey && clientMenuDialog.show) {
            setClientMenuDialog({ show: false, clientId: undefined });
        }
        previousScope.current = scopeKey;
    }, [sessionKey]);

    const isCurrent = (target: Target) => mounted.current && sessionRef.current === target.session;
    const captureTarget = (): Target | null => client && cohortId && clientMenuDialog.show
        ? { clientId: client.id, cohortId, session: sessionRef.current } : null;

    const handleClose = () => {
        sessionRef.current = { key: sessionKey };
        setConfirmation(null);
        setLoading(false);
        setClientMenuDialog({ show: false, clientId: undefined });
    }

    const handleRemoveClick = async () => {
        const target = captureTarget();
        if (!target || loading) return;
        setLoading(true);
        try {
            const preview = await getClientRemovalPreview(target.clientId, target.cohortId);
            if (!isCurrent(target)) return;
            if (preview.action !== 'archive') {
                setNoticeType(AlertBannerType.error);
                setNotice(preview.message);
                return;
            }
            setConfirmation({ target, message: preview.message });
        } catch {
            if (!isCurrent(target)) return;
            setNoticeType(AlertBannerType.error);
            setNotice('Unable to check whether this client can be archived.');
        } finally {
            if (isCurrent(target)) setLoading(false);
        }
    }

    const handleRemove = async () => {
        const target = confirmation?.target;
        if (!target || !isCurrent(target) || loading) return;
        setConfirmation(null);
        setLoading(true);
            try {
                const result = await removeClient(target.clientId, target.cohortId);
                if (!isCurrent(target)) return;
                if (result.action !== 'archive') {
                    setNoticeType(AlertBannerType.error);
                    setNotice(result.message || 'Client archival is not available.');
                    setLoading(false);
                    return;
                }
                setNoticeType(AlertBannerType.success);
                setNotice('Client archived in this cohort.');
            } catch (error) {
                if (!isCurrent(target)) return;
                const message = axios.isAxiosError<{ message?: string }>(error)
                    ? error.response?.data?.message
                    : undefined;
                setNoticeType(AlertBannerType.error);
                setNotice(message || 'Unable to archive client. Please try again.');
                setLoading(false);
                return;
            }
        setLoading(false);
        handleClose();
    }

    const handleRestore = async () => {
        const target = captureTarget();
        if (!target || loading) return;
        setLoading(true);
        try {
            await restoreClient(target.clientId, target.cohortId);
            if (!isCurrent(target)) return;
            setNoticeType(AlertBannerType.success);
            setNotice('Client restored to this cohort.');
            handleClose();
        } catch {
            if (!isCurrent(target)) return;
            setNoticeType(AlertBannerType.error);
            setNotice('Unable to restore client.');
        } finally {
            if (isCurrent(target)) setLoading(false);
        }
    }

    const feedback = (
        <AlertBanner
            type={noticeType}
            message={notice}
            setMessage={setNotice}
        />
    );

    return (
        <div>
            <Dialog open={!!clientMenuDialog.show} onClose={handleClose} keepMounted={false}>
                <DialogTitle>Client</DialogTitle>
                <Divider />
                <DialogContent sx={{ minWidth: isSmDown ? 300 : 500 }}>
                        {client?.tracked && (
                            <Grid item>
                                <TextField
                                    value={client.email || ''}
                                    label='Email'
                                    variant='standard'
                                    fullWidth
                                    margin='normal'
                                    InputProps={{ readOnly: true }}
                                />
                            </Grid>
                        )}
                        {client?.tracked && (<Box m={2} />)}
                        <Grid item>
                            <Box m={4} />
                            {cohortClientsEnabled && client?.cohort_client_state === 'ARCHIVED' ? (
                                <LoadingButton loading={loading} variant='contained' onClick={handleRestore} fullWidth>
                                    Restore to cohort
                                </LoadingButton>
                            ) : cohortClientsEnabled && client ? (
                                <LoadingButton loading={loading} variant='text' onClick={handleRemoveClick} color='error' fullWidth>
                                    Archive in this cohort
                                </LoadingButton>
                            ) : null}
                        </Grid>
                    {clientMenuDialog.show && feedback}
                </DialogContent>
            </Dialog>
            <ConfirmDialog
                open={!!confirmation && isCurrent(confirmation.target) && !!clientMenuDialog.show}
                title="Archive client in this cohort?"
                message={confirmation?.message || ''}
                confirmText="Archive"
                onConfirm={handleRemove}
                onCancel={() => {
                    setConfirmation(null);
                }}
            />
            {!clientMenuDialog.show && feedback}
        </div>
    )
}
